import type { TemplatizeReplacement, TemplatizeResult } from './templatizeTypes';

function countMatches(input: string, re: RegExp) {
  const matches = input.match(re);
  return matches?.length ?? 0;
}

function replaceAllWithCount(
  input: string,
  re: RegExp,
  replacement: string,
): { output: string; count: number } {
  const count = countMatches(input, re);
  if (count === 0) return { output: input, count: 0 };
  return { output: input.replace(re, replacement), count };
}

/**
 * Best-effort port of `misc-scripts/step_functions/state_templatize.sh`.
 *
 * This does NOT parse JSON; it operates on the raw text so it also works while
 * the user is still editing/invalid JSON.
 */
export function templatizeStepFunctionDefinition(definitionText: string): TemplatizeResult {
  let text = definitionText ?? '';
  const replacements: TemplatizeReplacement[] = [];

  // DETECTION is deliberately broader than rewriting: a bare `us-east-1` anywhere is worth warning
  // about, even where we will not rewrite it.
  const regionRe = /\bus-[a-z]{4}-[1-2]\b/g;
  const accountIdRe = /\b[0-9]{12}\b/g;
  // sed is `s/[0-9]\{12\}/${account_id}/g` — no word boundary, so it also rewrites a run adjacent
  // to a word character ("id-123456789012x"). \b-anchoring here left those untouched, which is a
  // divergence from what the backend used to commit. Note this consequently rewrites the first 12
  // digits of a longer run, exactly as sed did.
  const accountIdRewriteRe = /[0-9]{12}/g;
  // REWRITING mirrors the sed rule `s/\([.:]\)us-[a-z]\{4\}-[1-2]\([.:]\)/\1${region}\2/g`: only a
  // region sitting between `.` or `:` is substituted, so prose ("runs in us-east-1 now") is left
  // alone. Rewriting on \b instead diverged from what the backend actually wrote to the PR.
  const regionRewriteRe = /([.:])us-[a-z]{4}-[1-2]([.:])/g;

  const bucketPatterns: RegExp[] = [
    /\b(?:archived|staged|issued)-manuals-[a-z]+\b/g,
    /\blegacy-archived-manuals-[a-z]+\b/g,
    /\barchive-manual-migration-[a-z]+\b/g,
    /\bkms-manual-migration-[a-z]+\b/g,
    /\bkms-customer-data-migration-[a-z]+\b/g,
    /\bkms-lexipol-data-migration-[a-z]+\b/g,
    /\bphoenix-media-[a-z]+\b/g,
    /\bdocument-management-service-[a-z0-9-]+\b/g,
    /\bphx-job-service-[a-z]+\b/g,
  ];

  const domainRe = /\bclient-api(?:\.(?:dev|int|std|qa))?\.phoenix\.lexipol\.com\b/g;

  const regions = [...new Set(text.match(regionRe) ?? [])];
  const accountIds = [...new Set(text.match(accountIdRe) ?? [])];
  const buckets = [...new Set(bucketPatterns.flatMap(re => text.match(re) ?? []))];
  const domains = [...new Set(text.match(domainRe) ?? [])];

  const r1 = replaceAllWithCount(text, regionRewriteRe, '$1${region}$2');
  text = r1.output;
  if (r1.count > 0) {
    replacements.push({ from: 'us-<region>-<n>', to: '${region}', count: r1.count });
  }

  const r2 = replaceAllWithCount(text, accountIdRewriteRe, '${account_id}');
  text = r2.output;
  if (r2.count > 0) {
    replacements.push({ from: '<12-digit-account-id>', to: '${account_id}', count: r2.count });
  }

  const bucketRules: Array<{ re: RegExp; to: string; fromHint: string }> = [
    {
      re: /\barchived-manuals-[a-z]+\b/g,
      to: '${archived_bucket}',
      fromHint: 'archived-manuals-<env>',
    },
    { re: /\bstaged-manuals-[a-z]+\b/g, to: '${staged_bucket}', fromHint: 'staged-manuals-<env>' },
    { re: /\bissued-manuals-[a-z]+\b/g, to: '${issued_bucket}', fromHint: 'issued-manuals-<env>' },
    {
      re: /\blegacy-archived-manuals-[a-z]+\b/g,
      to: '${legacy_archived_bucket}',
      fromHint: 'legacy-archived-manuals-<env>',
    },
    {
      re: /\barchive-manual-migration-[a-z]+\b/g,
      to: '${archive_manual_migration_bucket}',
      fromHint: 'archive-manual-migration-<env>',
    },

    {
      re: /\bkms-manual-migration-[a-z]+\b/g,
      to: '${kms_manual_migration_bucket}',
      fromHint: 'kms-manual-migration-<env>',
    },
    {
      re: /\bkms-customer-data-migration-[a-z]+\b/g,
      to: '${kms_customer_data_bucket}',
      fromHint: 'kms-customer-data-migration-<env>',
    },
    {
      re: /\bkms-lexipol-data-migration-[a-z]+\b/g,
      to: '${kms_lexipol_data_bucket}',
      fromHint: 'kms-lexipol-data-migration-<env>',
    },

    { re: /\bphoenix-media-[a-z]+\b/g, to: '${phx_media_bucket}', fromHint: 'phoenix-media-<env>' },
    {
      re: /\bdocument-management-service-[a-z0-9-]+\b/g,
      to: '${document_management_service_bucket}',
      fromHint: 'document-management-service-<env>',
    },
    {
      re: /\bphx-job-service-[a-z]+\b/g,
      to: '${job_service_bucket}',
      fromHint: 'phx-job-service-<env>',
    },
  ];

  for (const rule of bucketRules) {
    const out = replaceAllWithCount(text, rule.re, rule.to);
    text = out.output;
    if (out.count > 0) {
      replacements.push({ from: rule.fromHint, to: rule.to, count: out.count });
    }
  }

  const rDomain = replaceAllWithCount(text, domainRe, '${api_domain_name}');
  text = rDomain.output;
  if (rDomain.count > 0) {
    replacements.push({
      from: 'client-api(.<env>)?.phoenix.lexipol.com',
      to: '${api_domain_name}',
      count: rDomain.count,
    });
  }

  return {
    outputText: text,
    replacements,
    hardcodedFindings: { regions, accountIds, buckets, domains },
  };
}
